import { produce, WritableDraft } from 'immer';
import { DatasourceUpgradeStatus } from '../datasourceUpgrade.types';
import {
  clearUnpublishedChanges,
  clearUpgradePresetVersion,
  clearUpgradeStatusObject,
  setUnpublishedChanges,
  setUpgradePresetVersion,
  setUpgradeStatusObject,
} from '../../uiStatus';
import { bumpVersion } from '../../datasourceUtils';
import { Datasource, Datasource_Preset } from '@/types/api';

/***
 * Clear the datasource upgrade status and preset version. Used by both publish and abort.
 * @param datasource - The datasource to clear the upgrade status and preset version fromatus to clear
 * @param datasourceInitial - The initial datasource
 * @returns The datasource with the upgrade status and preset version cleared
 */
export const clearDatasourceUpgrade = (
  datasource: WritableDraft<Datasource>,
  datasourceInitial: WritableDraft<Datasource>,
) => {
  // this is correct for abort too since datasource is also the initial datasource
  let newDatasource = produce(datasource, (draft) => {
    if (!draft.metadata) {
      draft.metadata = {
        name: '',
      };
    }

    draft.metadata = {
      ...draft.metadata,
      // we need to keep the version from the initial datasource version
      version: datasourceInitial.metadata?.version ?? 0,
      // we also need to keep the uiStatus from the initial datasource in case there are other values to preserve
      uiStatus: datasourceInitial.metadata?.uiStatus,
    };
  });

  // now we can clear the unpublished changes, bump the version, and clear the upgrade status and preset version
  newDatasource = clearUnpublishedChanges(newDatasource);
  newDatasource = bumpVersion(newDatasource);
  newDatasource = clearUpgradeStatusObject(newDatasource);
  newDatasource = clearUpgradePresetVersion(newDatasource);

  return newDatasource;
};

export const updateDatasourceUpgrade = (
  datasource: WritableDraft<Datasource>,
  upgradeStatus: DatasourceUpgradeStatus | undefined,
  upgradePreset: Datasource_Preset,
  datasourceInitial: WritableDraft<Datasource>,
) => {
  if (
    !upgradeStatus ||
    !upgradePreset.versionNumber ||
    !datasourceInitial.metadata?.name
  ) {
    throw new Error(
      `Unable to save datasource "${datasourceInitial.metadata?.name}" upgrade to version "${upgradePreset.versionNumber}". Missing required fields.`,
    );
  }

  // now we can set the unpublished changes, bump the version, and set the upgrade status and preset version
  let newDatasource = setUnpublishedChanges(datasourceInitial, datasource);
  newDatasource = bumpVersion(newDatasource);
  newDatasource = setUpgradeStatusObject(newDatasource, upgradeStatus);
  newDatasource = setUpgradePresetVersion(
    newDatasource,
    upgradePreset.versionNumber,
  );

  return newDatasource;
};
